import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  writeBatch,
  deleteDoc,
} from '@angular/fire/firestore';

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;        // ruta al asset (svg/png)
  unlocked: boolean;   // true cuando está conseguida
  createdAt?: number;  // timestamp cuando se consiguió
}

type BadgeCatalog = Record<string, Omit<Badge, 'unlocked' | 'createdAt'>>;

@Injectable({ providedIn: 'root' })
export class BadgesService {
  private firestore = inject(Firestore);
  private toast = inject(ToastController);

  /*
   * Catálogo estático de insignias del MVP.
   * (1, 5, 10, 20) + (likes/impulsos 20/50/100 recibidos + 20/50/100 dados)
   */
  private CATALOG: BadgeCatalog = {
    pionero: {
      id: 'pionero',
      name: 'Pionero',
      description: 'Completaste tu primer reto',
      icon: 'assets/badges/pionero.svg',
    },

    constancia_5: {
      id: 'constancia_5',
      name: 'Constancia 5',
      description: 'Completaste 5 retos',
      icon: 'assets/badges/constancia_5.svg',
    },
    constancia_10: {
      id: 'constancia_10',
      name: 'Constancia 10',
      description: 'Completaste 10 retos',
      icon: 'assets/badges/constancia_10.svg',
    },
    leyenda_20: {
      id: 'leyenda_20',
      name: 'Leyenda 20',
      description: '¡Completaste toda la secuencia de 20 retos!',
      icon: 'assets/badges/leyenda_20.svg',
    },

    // ❤️ Insignias por IMPULSOS / "LIKES" RECIBIDOS (total en tu cuenta)
    likes_recibidos_20: {
      id: 'likes_recibidos_20',
      name: 'Aplausos 20',
      description: 'Tus fotos han recibido 20 likes',
      icon: 'assets/badges/likes_recibidos_20.svg',
    },
    likes_recibidos_50: {
      id: 'likes_recibidos_50',
      name: 'Reconocido 50',
      description: 'Tus fotos han recibido 50 likes',
      icon: 'assets/badges/likes_recibidos_50.svg',
    },
    likes_recibidos_100: {
      id: 'likes_recibidos_100',
      name: 'Top 100',
      description: 'Tus fotos han recibido 100 likes',
      icon: 'assets/badges/likes_recibidos_100.svg',
    },

    // 🤝 Insignias por IMPULSOS / "LIKES" DADOS (tú a los demás)
    likes_dados_20: {
      id: 'likes_dados_20',
      name: 'Buen Impulso 20',
      description: 'Has dado 20 likes',
      icon: 'assets/badges/likes_dados_20.svg',
    },
    likes_dados_50: {
      id: 'likes_dados_50',
      name: 'Apoyo 50',
      description: 'Has dado 50 likes',
      icon: 'assets/badges/likes_dados_50.svg',
    },
    likes_dados_100: {
      id: 'likes_dados_100',
      name: 'Impulsor 100',
      description: 'Has dado 100 likes',
      icon: 'assets/badges/likes_dados_100.svg',
    },
  };

  /** ✅ Durante reset no concedemos insignias (evita que reaparezcan). */
  private isResetting(): boolean {
    try {
      return (
        localStorage.getItem('challup_resetting') === '1' ||
        sessionStorage.getItem('challup_resetting') === '1'
      );
    } catch {
      return false;
    }
  }

  /** Devuelve el catálogo base (sin estado de desbloqueo). */
  getBadges(): Badge[] {
    return Object.values(this.CATALOG).map((b) => ({
      ...b,
      unlocked: false,
    }));
  }

  /** Ref a /userBadges/{uid}/earned/{badgeId} */
  private userBadgeRef(uid: string, badgeId: string) {
    return doc(this.firestore, `userBadges/${uid}/earned/${badgeId}`);
  }

  /** ¿El usuario ya tiene esta insignia? */
  async userHasBadge(uid: string, badgeId: string): Promise<boolean> {
    if (!uid) return false;
    const snap = await getDoc(this.userBadgeRef(uid, badgeId));
    return snap.exists();
  }

  /**
   * Concede una insignia (upsert).
   * ✅ Si estamos en reset -> NO hace nada.
   *
   * IMPORTANTE:
   * ✅ Idempotente: si ya existe en Firestore -> NO re-escribe y NO muestra toast.
   *
   * @param alreadyChecked Si el caller ya comprobó userHasBadge (evita doble lectura).
   */
  async grantBadge(
    uid: string,
    badgeId: keyof BadgeCatalog,
    showToast = true,
    alreadyChecked = false
  ): Promise<Badge | null> {
    if (!uid) return null;
    if (this.isResetting()) return null;

    const base = this.CATALOG[badgeId];
    if (!base) return null;

    const ref = this.userBadgeRef(uid, badgeId);

    // ✅ Si el caller NO ha chequeado, lo hacemos aquí para evitar re-toasts tras logout/reinstalación.
    if (!alreadyChecked) {
      const snap = await getDoc(ref);
      if (snap.exists()) {
        // Ya la tenía -> no toast, no reescritura, no tocar createdAt
        return null;
      }
    }

    const payload: Badge = {
      ...base,
      unlocked: true,
      createdAt: Date.now(),
    };

    await setDoc(ref, payload, { merge: true });

    if (showToast) {
      await this.presentBadgeUnlockToast(payload);
    }

    return payload;
  }

  /**
   * ✅ Toast de DESBLOQUEO (cuando consigues una insignia)
   * - icon + botón cerrar
   * - position top
   * - cssClass por tipo para meter gradiente/glow desde global.scss
   */
  private async presentBadgeUnlockToast(badge: Badge) {
    try {
      const { message, css, icon } = this.buildUnlockToastForBadge(badge);

      const t = await this.toast.create({
        message,
        duration: 3400,
        position: 'top',
        icon,
        cssClass: css,
        buttons: [
          {
            side: 'end',
            icon: 'close',
            role: 'cancel',
          },
        ],
      });

      await t.present();
    } catch (e) {
      console.warn('Badge unlock toast error (ignorado):', e);
    }
  }

  /**
   * ✅ Toast de INFO (cuando pulsas una insignia en Profile > Insignias)
   * Como lo quieres:
   * - abajo (bottom)
   * - solo texto
   * - sin icono
   * - sin botón cerrar
   * - sin haptics
   */
  async presentBadgeInfoToast(badge: Badge) {
    if (!badge) return;

    try {
      const { message, css } = this.buildInfoToastForBadge(badge);

      const t = await this.toast.create({
        message,
        duration: 2000,
        position: 'bottom',
        cssClass: css,
      });

      await t.present();
    } catch (e) {
      console.warn('Badge info toast error (ignorado):', e);
    }
  }

  private buildUnlockToastForBadge(badge: Badge): { message: string; css: string[]; icon: string } {
    // Base
    let message = `🏅 Insignia conseguida: ${badge.name} — ${badge.description}`;
    let css: string[] = ['badge-toast', 'badge-toast--unlock'];
    let icon = 'ribbon-outline';

    const isLikesRec = badge.id.startsWith('likes_recibidos_');
    const isLikesGiven = badge.id.startsWith('likes_dados_');

    // Core / progreso
    if (badge.id === 'pionero') {
      message = '¡Insignia desbloqueada! Has participado en tu primer reto en ChallUp ✨';
      css.push('badge-toast--pionero');
      icon = 'sparkles-outline';
      return { message, css, icon };
    }

    if (badge.id === 'constancia_5') {
      message = '🔥 Constancia 5 — Has completado 5 retos. ¡Vamos!';
      css.push('badge-toast--progress');
      icon = 'flame-outline';
      return { message, css, icon };
    }

    if (badge.id === 'constancia_10') {
      message = '⚡ Constancia 10 — Ya llevas 10 retos. Eres imparable.';
      css.push('badge-toast--progress');
      icon = 'flash-outline';
      return { message, css, icon };
    }

    if (badge.id === 'leyenda_20') {
      message = '🚀 Leyenda 20 — ¡Has completado los 20 retos! Increíble.';
      css.push('badge-toast--legend');
      icon = 'rocket-outline';
      return { message, css, icon };
    }

    // ✅ Likes/impulsos (lo que pediste)
    if (isLikesRec) {
      const n = this.extractThreshold(badge.id); // 20/50/100
      message = n > 0
        ? `👏 Tus fotos han recibido ${n} likes.`
        : '👏 Tus fotos han recibido likes.';

      if (n >= 100) message += ' Brutal. 👑';
      else if (n >= 50) message += ' Muy bien. 🙌';
      else if (n >= 20) message += ' ¡Vamos! 🔥';

      css.push('badge-toast--likes-received');
      icon = 'heart-outline';
      return { message, css, icon };
    }

    if (isLikesGiven) {
      const n = this.extractThreshold(badge.id); // 20/50/100
      message = n > 0
        ? `🤝 Has dado ${n} likes.`
        : '🤝 Has dado likes.';

      if (n >= 100) message += ' Pura energía. 💥';
      else if (n >= 50) message += ' Estás a tope. ⚡';
      else if (n >= 20) message += ' Buen ojo. ✨';

      css.push('badge-toast--likes-given');
      icon = 'thumbs-up-outline';
      return { message, css, icon };
    }

    css.push('badge-toast--generic');
    return { message, css, icon };
  }

  private buildInfoToastForBadge(badge: Badge): { message: string; css: string[] } {
    // ✅ Solo texto (lo que quieres ver al pulsar)
    const message = `${badge.name} — ${badge.description}`;
    const css: string[] = ['badge-toast', 'badge-toast--info'];
    return { message, css };
  }

  private extractThreshold(badgeId: string): number {
    const m = badgeId.match(/_(\d+)$/);
    if (!m) return 0;
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : 0;
  }

  /**
   * MVP:
   * Si el usuario acaba de realizar su PRIMER envío (totalSubmissions === 1),
   * concede "Pionero" SOLO SI NO LA TENÍA YA.
   * ✅ Si estamos reseteando -> NO hace nada.
   */
  async grantPioneroIfFirstSubmission(uid: string, totalSubmissions: number) {
    if (!uid) return;
    if (this.isResetting()) return;

    if (totalSubmissions === 1) {
      const already = await this.userHasBadge(uid, 'pionero');
      if (!already) {
        // ✅ Ya hemos comprobado arriba, evitamos doble lectura
        await this.grantBadge(uid, 'pionero', true, true);
      }
    }
  }

  /**
   * checkUnlocks(completedCount, uid)
   * ✅ 5, 10, 20
   * ✅ solo una vez cada insignia
   * ✅ Si estamos reseteando -> devuelve vacío
   */
  async checkUnlocks(completedCount: number, uid: string): Promise<Badge[]> {
    const newlyUnlocked: Badge[] = [];

    if (!uid) return newlyUnlocked;
    if (this.isResetting()) return newlyUnlocked;

    if (!Number.isFinite(completedCount) || completedCount <= 0) {
      return newlyUnlocked;
    }

    const rules: Array<{ id: keyof BadgeCatalog; param: number }> = [
      { id: 'constancia_5', param: 5 },
      { id: 'constancia_10', param: 10 },
      { id: 'leyenda_20', param: 20 },
    ];

    for (const r of rules) {
      if (completedCount >= r.param) {
        const already = await this.userHasBadge(uid, r.id);
        if (already) continue;

        // ✅ Ya hemos comprobado arriba, evitamos doble lectura
        const got = await this.grantBadge(uid, r.id, true, true);
        if (got) newlyUnlocked.push(got);
      }
    }

    return newlyUnlocked;
  }

  /**
   * ✅ Insignias por impulsos/likes acumulados.
   */
  async checkLikeUnlocks(
    uid: string,
    receivedTotal: number,
    givenTotal: number
  ): Promise<Badge[]> {
    const newlyUnlocked: Badge[] = [];

    if (!uid) return newlyUnlocked;
    if (this.isResetting()) return newlyUnlocked;

    const safeReceived = Number.isFinite(receivedTotal) ? receivedTotal : 0;
    const safeGiven = Number.isFinite(givenTotal) ? givenTotal : 0;

    const rules: Array<{ id: keyof BadgeCatalog; param: number; value: number }> = [
      { id: 'likes_recibidos_20', param: 20, value: safeReceived },
      { id: 'likes_recibidos_50', param: 50, value: safeReceived },
      { id: 'likes_recibidos_100', param: 100, value: safeReceived },

      { id: 'likes_dados_20', param: 20, value: safeGiven },
      { id: 'likes_dados_50', param: 50, value: safeGiven },
      { id: 'likes_dados_100', param: 100, value: safeGiven },
    ];

    for (const r of rules) {
      if (r.value >= r.param) {
        const already = await this.userHasBadge(uid, r.id);
        if (already) continue;

        // ✅ Ya hemos comprobado arriba, evitamos doble lectura
        const got = await this.grantBadge(uid, r.id, true, true);
        if (got) newlyUnlocked.push(got);
      }
    }

    return newlyUnlocked;
  }

  /**
   * Recupera las insignias desbloqueadas del usuario (ordenadas por fecha).
   */
  async getUserBadges(uid: string): Promise<Badge[]> {
    if (!uid) return [];

    const col = collection(this.firestore, `userBadges/${uid}/earned`);
    const qs = await getDocs(col);

    const list = qs.docs.map((d) => {
      const data = d.data() as Partial<Badge>;
      return { ...data, id: data.id ?? d.id };
    });

    const merged: Badge[] = list.map((partial) => {
      const base = this.CATALOG[(partial.id as keyof BadgeCatalog) ?? ''] ?? null;
      return {
        id: partial.id!,
        name: partial.name ?? base?.name ?? 'Insignia',
        description: partial.description ?? base?.description ?? '',
        icon: partial.icon ?? base?.icon ?? '',
        unlocked: true,
        createdAt: partial.createdAt ?? 0,
      };
    });

    return merged.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  }

  /**
   * Borra TODAS las insignias de un usuario (para resetAll).
   */
  async resetUserBadges(uid: string): Promise<void> {
    if (!uid) return;

    const col = collection(this.firestore, `userBadges/${uid}/earned`);
    const qs = await getDocs(col);

    if (!qs.empty) {
      const batch = writeBatch(this.firestore);
      qs.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }

    await deleteDoc(doc(this.firestore, `userBadges/${uid}`)).catch(() => {});
  }
}
